import type {
  SocialDateRange,
  SocialOverview,
  SocialPlatform,
  SocialSourceFailure,
} from '../../types/socialReporting.js';
import {
  latestInRange,
  mergeFollowerSeries,
  runningCountDelta,
  sumSeriesInRange,
  type FollowerTrendRow,
} from './socialSeries.js';
import { buildGbpHeadlineKpis } from './socialGbp.js';

// Organic Social Reporting — Overview view model (pure).
//
// Turns the raw SocialOverview (live Zernio reads, normalized on the server) into
// the executive scorecard: 6 hero KPIs with period-over-period deltas, the FB/IG
// follower trend, and the per-platform mini-cards. All aggregation lives here so
// it's unit-testable off the observed fixtures (observed-2026-07-22).
//
// ═══ THE GATE THIS MODULE ENFORCES (mutation-tested) ═══
// Google Business has NO analytics — zero post rows, no insights endpoint (probe
// B2). Facebook Stories are publishable but not measurable (no FB story insights).
// A metric that cannot be measured renders "not measurable", NEVER 0 — zero
// engagement and no measuring instrument are different claims to a client. The
// reach/engagement KPIs and the GBP mini-card are gated on isMeasurablePlatform;
// deleting that gate must fail a test.

// ---------------------------------------------------------------------------
// the not-measurable gate
// ---------------------------------------------------------------------------

/**
 * Platforms Zernio can report account/post analytics on. Google Business is
 * deliberately absent — it has no analytics surface at all. This is the single
 * chokepoint the "not measurable" rule flows from.
 */
const MEASURABLE_PLATFORMS: SocialPlatform[] = ['facebook', 'instagram'];

export function isMeasurablePlatform(platform: SocialPlatform): boolean {
  return MEASURABLE_PLATFORMS.includes(platform);
}

/** Whether ANY connected platform can supply reach/engagement/follower metrics. */
export function hasMeasurablePlatform(connected: Record<SocialPlatform, boolean>): boolean {
  return (Object.keys(connected) as SocialPlatform[]).some(
    (p) => connected[p] && isMeasurablePlatform(p),
  );
}

export const GBP_NOT_MEASURABLE_NOTE = 'Not measurable for Google Business';
export const FB_STORY_NOT_MEASURABLE_NOTE = 'Stories — insights not available on Facebook';

// ---------------------------------------------------------------------------
// KPI card model
// ---------------------------------------------------------------------------

export type KpiFormat = 'number' | 'percent' | 'signed';

export type KpiState =
  /** A real figure. */
  | 'measured'
  /** Genuinely can't be measured (e.g. GBP-only client). Renders a note, never 0. */
  | 'not-measurable'
  /** Sourced elsewhere, not yet on this surface (Community handled → Community tab). */
  | 'pending';

export interface KpiCard {
  key: string;
  label: string;
  /** null unless state === 'measured'. Never render null as 0. */
  value: number | null;
  format: KpiFormat;
  previous: number | null;
  /** null when there's no comparable previous (previous 0 or absent). */
  deltaPct: number | null;
  direction: 'up' | 'down' | 'flat' | null;
  state: KpiState;
  note: string | null;
}

/** Percentage change vs the previous period, and its direction. */
export function periodDelta(
  current: number | null,
  previous: number | null,
): { deltaPct: number | null; direction: 'up' | 'down' | 'flat' | null } {
  if (current === null || previous === null) return { deltaPct: null, direction: null };
  if (previous === 0) {
    // No baseline to compute a % against. Direction is still meaningful.
    return { deltaPct: null, direction: current > 0 ? 'up' : 'flat' };
  }
  const deltaPct = ((current - previous) / Math.abs(previous)) * 100;
  const direction = deltaPct > 0 ? 'up' : deltaPct < 0 ? 'down' : 'flat';
  return { deltaPct, direction };
}

function measuredCard(
  key: string,
  label: string,
  format: KpiFormat,
  current: number,
  previous: number,
): KpiCard {
  const { deltaPct, direction } = periodDelta(current, previous);
  return { key, label, value: current, format, previous, deltaPct, direction, state: 'measured', note: null };
}

function notMeasurableCard(key: string, label: string, format: KpiFormat, note: string): KpiCard {
  return { key, label, value: null, format, previous: null, deltaPct: null, direction: null, state: 'not-measurable', note };
}

function pendingCard(key: string, label: string, format: KpiFormat, note: string): KpiCard {
  return { key, label, value: null, format, previous: null, deltaPct: null, direction: null, state: 'pending', note };
}

// ---------------------------------------------------------------------------
// metric aggregation (combines FB + IG per the brief's definitions)
// ---------------------------------------------------------------------------

// Reach: FB `page_media_view` (the daily FB reach line per the brief) + IG `reach`
// (the ONE IG daily metric). Both daily → summable per window.
function reachInRange(data: SocialOverview, range: SocialDateRange): number {
  return (
    sumSeriesInRange(data.facebook?.pageMediaView ?? null, range) +
    sumSeriesInRange(data.instagram?.reach ?? null, range)
  );
}

// Engagement: FB `page_post_engagements` (daily) + IG `total_interactions`
// (total_value ONLY — a period total, not a series, so current vs previous come
// from the two windows the server fetched separately).
function engagementInRange(data: SocialOverview, which: 'current' | 'previous'): number {
  const range = which === 'current' ? data.range : data.previousRange;
  const fb = sumSeriesInRange(data.facebook?.pagePostEngagements ?? null, range);
  const igTotals = which === 'current' ? data.instagram?.totalsCurrent : data.instagram?.totalsPrevious;
  const ig = igTotals?.totalInteractions ?? 0;
  return fb + ig;
}

// Net follower growth per platform: prefer the synthesized gained/lost sum; fall
// back to the running-count delta when those series are empty (the documented
// possibility for a Zernio-synthesized metric). Returns 0 when nothing measurable.
function followerNet(
  gained: import('../../types/socialReporting.js').SocialMetricSeries | null,
  lost: import('../../types/socialReporting.js').SocialMetricSeries | null,
  runningCount: import('../../types/socialReporting.js').SocialMetricSeries | null,
  range: SocialDateRange,
): number {
  const hasGainedLost =
    (gained?.values.length ?? 0) > 0 || (lost?.values.length ?? 0) > 0;
  if (hasGainedLost) {
    return sumSeriesInRange(gained, range) - sumSeriesInRange(lost, range);
  }
  return runningCountDelta(runningCount, range) ?? 0;
}

function followerNetTotal(data: SocialOverview, range: SocialDateRange): number {
  let net = 0;
  if (data.facebook) {
    net += followerNet(
      data.facebook.followersGained,
      data.facebook.followersLost,
      data.facebook.pageFollows,
      range,
    );
  }
  if (data.instagram) {
    net += followerNet(
      data.instagram.followersGained,
      data.instagram.followersLost,
      data.instagram.followerCount,
      range,
    );
  }
  return net;
}

/** Count published posts whose publish day falls inside the window (inclusive). */
function postsPublishedInRange(data: SocialOverview, range: SocialDateRange): number {
  let n = 0;
  for (const post of data.publishedPosts) {
    const day = post.publishedAt.slice(0, 10);
    if (day >= range.from && day <= range.to) n += 1;
  }
  return n;
}

// ---------------------------------------------------------------------------
// hero cards
// ---------------------------------------------------------------------------

/**
 * The 6 executive KPIs, in scorecard order. Reach / Engagement / Engagement Rate
 * / Follower growth are gated on a measurable platform (a GBP-only client gets a
 * "not measurable" card, never a zero). Posts published is always measurable
 * (native history covers all three platforms). Community handled is pending until
 * the Community & Reputation tab lands.
 */
export function buildHeroCards(data: SocialOverview): KpiCard[] {
  const measurable = hasMeasurablePlatform(data.connected);

  const reachCard = measurable
    ? measuredCard('reach', 'Reach', 'number', reachInRange(data, data.range), reachInRange(data, data.previousRange))
    : notMeasurableCard('reach', 'Reach', 'number', GBP_NOT_MEASURABLE_NOTE);

  const engCurrent = engagementInRange(data, 'current');
  const engPrevious = engagementInRange(data, 'previous');
  const engagementCard = measurable
    ? measuredCard('engagement', 'Engagement', 'number', engCurrent, engPrevious)
    : notMeasurableCard('engagement', 'Engagement', 'number', GBP_NOT_MEASURABLE_NOTE);

  // Engagement rate = engagement / reach, recomputed from the summed numerator +
  // denominator (NOT an average of daily rates), matching the ads reporting.
  const reachCurrent = reachInRange(data, data.range);
  const reachPrevious = reachInRange(data, data.previousRange);
  const rateCurrent = reachCurrent > 0 ? (engCurrent / reachCurrent) * 100 : null;
  const ratePrevious = reachPrevious > 0 ? (engPrevious / reachPrevious) * 100 : null;
  const rateCard = !measurable
    ? notMeasurableCard('engagement_rate', 'Engagement rate', 'percent', GBP_NOT_MEASURABLE_NOTE)
    : rateCurrent === null
      ? notMeasurableCard('engagement_rate', 'Engagement rate', 'percent', 'No reach recorded this period')
      : measuredCard('engagement_rate', 'Engagement rate', 'percent', rateCurrent, ratePrevious ?? 0);

  const followerCard = measurable
    ? measuredCard(
        'follower_growth',
        'Follower growth',
        'signed',
        followerNetTotal(data, data.range),
        followerNetTotal(data, data.previousRange),
      )
    : notMeasurableCard('follower_growth', 'Follower growth', 'signed', GBP_NOT_MEASURABLE_NOTE);

  const postsCard = measuredCard(
    'posts_published',
    'Posts published',
    'number',
    postsPublishedInRange(data, data.range),
    postsPublishedInRange(data, data.previousRange),
  );

  // Community handled = total inbound (comments+DMs+reviews) from OUR inbox, via
  // the SAME seam as the Community & Reputation tab (so the KPI equals that tab's
  // "Total handled"). null ONLY when the community read failed → a pending card;
  // a no-activity account is a measured 0 (it's our own count, always measurable).
  const communityCard =
    data.communityHandled === null
      ? pendingCard('community_handled', 'Community handled', 'number', 'Shown in the Community & Reputation tab')
      : measuredCard(
          'community_handled',
          'Community handled',
          'number',
          data.communityHandled.current,
          data.communityHandled.previous,
        );

  return [reachCard, engagementCard, rateCard, followerCard, postsCard, communityCard];
}

// ---------------------------------------------------------------------------
// follower trend
// ---------------------------------------------------------------------------

export interface FollowerTrendView {
  rows: FollowerTrendRow[];
  hasFacebook: boolean;
  hasInstagram: boolean;
  isEmpty: boolean;
}

export function buildFollowerTrend(data: SocialOverview): FollowerTrendView {
  const fb = data.facebook?.pageFollows ?? null;
  const ig = data.instagram?.followerCount ?? null;
  const rows = mergeFollowerSeries(fb, ig, data.range);
  return {
    rows,
    hasFacebook: rows.some((r) => r.facebook !== null),
    hasInstagram: rows.some((r) => r.instagram !== null),
    isEmpty: rows.length === 0,
  };
}

// ---------------------------------------------------------------------------
// per-platform mini-cards
// ---------------------------------------------------------------------------

// A period-total metric with its period-over-period delta — for the IG card's
// total_value metrics (views / accounts engaged / total interactions), which have
// no daily series so they're shown as totals, not lines.
export interface PeriodStat {
  key: string;
  label: string;
  current: number;
  previous: number | null;
  deltaPct: number | null;
  direction: 'up' | 'down' | 'flat' | null;
}

export interface PlatformMiniCard {
  platform: SocialPlatform;
  measurable: boolean;
  /** Present for measurable platforms. */
  reach: number | null;
  /** FB: page_post_engagements sum. IG: null (its interactions live in periodStats). */
  engagement: number | null;
  followerNet: number | null;
  /** Gained / lost split under the net — null when the synthesized series is absent. */
  followersGained: number | null;
  followersLost: number | null;
  currentFollowers: number | null;
  /** Always available (native history covers every platform incl. GBP). */
  postsPublished: number;
  /** IG total_value period totals (views / accounts engaged / total interactions). */
  periodStats: PeriodStat[];
  /**
   * GBP headline KPIs (total impressions / calls / directions / website clicks with
   * deltas) — present only on the Google Business card when its performance loaded.
   * When set, the card renders these instead of the "not measurable" note.
   */
  gbpStats: PeriodStat[] | null;
  /** The "not measurable" line for GBP; null for FB/IG (and null on a GBP card with stats). */
  note: string | null;
}

function platformPostsPublished(data: SocialOverview, platform: SocialPlatform): number {
  let n = 0;
  for (const post of data.publishedPosts) {
    if (post.platform !== platform) continue;
    const day = post.publishedAt.slice(0, 10);
    if (day >= data.range.from && day <= data.range.to) n += 1;
  }
  return n;
}

// Gained/lost split from the synthesized daily series. Null when the series is
// absent (so the card shows just the net, never fabricated 0/0 halves).
function gainedLostSplit(
  gained: import('../../types/socialReporting.js').SocialMetricSeries | null,
  lost: import('../../types/socialReporting.js').SocialMetricSeries | null,
  range: SocialDateRange,
): { gained: number | null; lost: number | null } {
  const hasSeries = (gained?.values.length ?? 0) > 0 || (lost?.values.length ?? 0) > 0;
  if (!hasSeries) return { gained: null, lost: null };
  return { gained: sumSeriesInRange(gained, range), lost: sumSeriesInRange(lost, range) };
}

// Build a period-total stat (current vs previous). Null when the metric is absent.
function periodStat(
  key: string,
  label: string,
  current: number | null,
  previous: number | null,
): PeriodStat | null {
  if (current === null) return null;
  const { deltaPct, direction } = periodDelta(current, previous);
  return { key, label, current, previous, deltaPct, direction };
}

const EMPTY_CARD = {
  reach: null,
  engagement: null,
  followerNet: null,
  followersGained: null,
  followersLost: null,
  currentFollowers: null,
  periodStats: [] as PeriodStat[],
  gbpStats: null as PeriodStat[] | null,
};

export function buildPlatformCards(data: SocialOverview): PlatformMiniCard[] {
  const order: SocialPlatform[] = ['facebook', 'instagram', 'googlebusiness'];
  const cards: PlatformMiniCard[] = [];
  for (const platform of order) {
    if (!data.connected[platform]) continue;
    const postsPublished = platformPostsPublished(data, platform);

    if (!isMeasurablePlatform(platform)) {
      // GBP: real KPIs when its performance loaded (total impressions / calls /
      // directions / website clicks with deltas); otherwise the not-measurable note
      // (never a zero). NOT gated on xCapabilities — that flag reads false yet the
      // endpoint returns data. GBP genuinely has no reach/engagement/followers, so
      // the hero Reach/Engagement cards stay not-measurable; only this card gains KPIs.
      const gbpStats: PeriodStat[] | null = data.googleBusiness
        ? buildGbpHeadlineKpis(data.googleBusiness.metrics, data.range, data.previousRange)
        : null;
      cards.push({
        platform,
        measurable: false,
        ...EMPTY_CARD,
        gbpStats,
        postsPublished,
        note: gbpStats ? null : GBP_NOT_MEASURABLE_NOTE,
      });
      continue;
    }

    if (platform === 'facebook' && data.facebook) {
      const split = gainedLostSplit(data.facebook.followersGained, data.facebook.followersLost, data.range);
      cards.push({
        platform,
        measurable: true,
        reach: sumSeriesInRange(data.facebook.pageMediaView, data.range),
        engagement: sumSeriesInRange(data.facebook.pagePostEngagements, data.range),
        followerNet: followerNet(
          data.facebook.followersGained,
          data.facebook.followersLost,
          data.facebook.pageFollows,
          data.range,
        ),
        followersGained: split.gained,
        followersLost: split.lost,
        currentFollowers: latestInRange(data.facebook.pageFollows, data.range),
        postsPublished,
        periodStats: [], // FB has no total_value period metrics on the card
        gbpStats: null,
        note: null,
      });
    } else if (platform === 'instagram' && data.instagram) {
      const split = gainedLostSplit(data.instagram.followersGained, data.instagram.followersLost, data.range);
      const tc = data.instagram.totalsCurrent;
      const tp = data.instagram.totalsPrevious;
      // IG total_value metrics as period totals with deltas (views / accounts
      // engaged / total interactions). Total interactions IS the IG "engagement",
      // so it lives HERE (with a delta) rather than a duplicate engagement tile.
      const periodStats = [
        periodStat('views', 'Views', tc?.views ?? null, tp?.views ?? null),
        periodStat('accounts_engaged', 'Accounts engaged', tc?.accountsEngaged ?? null, tp?.accountsEngaged ?? null),
        periodStat('total_interactions', 'Total interactions', tc?.totalInteractions ?? null, tp?.totalInteractions ?? null),
      ].filter((s): s is PeriodStat => s !== null);
      cards.push({
        platform,
        measurable: true,
        reach: sumSeriesInRange(data.instagram.reach, data.range),
        engagement: null, // → Total interactions in periodStats
        followerNet: followerNet(
          data.instagram.followersGained,
          data.instagram.followersLost,
          data.instagram.followerCount,
          data.range,
        ),
        followersGained: split.gained,
        followersLost: split.lost,
        currentFollowers: latestInRange(data.instagram.followerCount, data.range),
        postsPublished,
        periodStats,
        gbpStats: null,
        note: null,
      });
    } else {
      // Connected but its insights failed to load — show cadence, no fake zeros.
      cards.push({ platform, measurable: true, ...EMPTY_CARD, postsPublished, note: null });
    }
  }
  return cards;
}

// ---------------------------------------------------------------------------
// top-level view
// ---------------------------------------------------------------------------

export interface SocialOverviewView {
  heroCards: KpiCard[];
  followerTrend: FollowerTrendView;
  platformCards: PlatformMiniCard[];
  /** The as-of anchor (getAnalytics overview.lastSync). */
  asOf: string | null;
  failures: SocialSourceFailure[];
}

export function buildSocialOverviewView(data: SocialOverview): SocialOverviewView {
  return {
    heroCards: buildHeroCards(data),
    followerTrend: buildFollowerTrend(data),
    platformCards: buildPlatformCards(data),
    asOf: data.lastSync,
    failures: data.failures,
  };
}

// ---------------------------------------------------------------------------
// display formatting
// ---------------------------------------------------------------------------

/** Format a KPI value for display (null → em dash — never 0). */
export function formatKpiValue(card: KpiCard): string {
  if (card.value === null) return '—';
  switch (card.format) {
    case 'percent':
      return `${card.value.toFixed(2)}%`;
    case 'signed':
      return `${card.value > 0 ? '+' : ''}${Math.round(card.value).toLocaleString()}`;
    case 'number':
    default:
      return Math.round(card.value).toLocaleString();
  }
}

/** Format a period-over-period delta (e.g. "+12.4%"), or null when incomparable. */
export function formatDelta(card: KpiCard): string | null {
  if (card.deltaPct === null) return null;
  const sign = card.deltaPct > 0 ? '+' : '';
  return `${sign}${card.deltaPct.toFixed(1)}%`;
}

// ---------------------------------------------------------------------------
// "what happened this month" — the hand-bound narrative slot (AI later)
// ---------------------------------------------------------------------------

const DIRECTION_WORD: Record<'up' | 'down' | 'flat', string> = {
  up: 'up',
  down: 'down',
  flat: 'flat',
};

/**
 * A plain-language summary of the period, built deterministically from the
 * measured KPIs. v1 is this hand-binding; the slot is the natural home for AI
 * generation later. Not-measurable / pending KPIs are skipped (never narrated as
 * zero). Returns an empty array when nothing is measurable (a GBP-only client's
 * story lives in Community & Reputation, not here).
 */
export function buildOverviewNarrative(cards: KpiCard[]): string[] {
  const byKey = Object.fromEntries(cards.map((c) => [c.key, c]));
  const out: string[] = [];

  const phrase = (card: KpiCard | undefined, noun: string): string | null => {
    if (!card || card.state !== 'measured' || card.value === null) return null;
    const value = formatKpiValue(card);
    const delta = formatDelta(card);
    if (delta && card.direction && card.direction !== 'flat') {
      return `${noun} was ${value}, ${DIRECTION_WORD[card.direction]} ${delta} on the previous period`;
    }
    return `${noun} was ${value}`;
  };

  const reach = phrase(byKey.reach, 'Reach');
  const engagement = phrase(byKey.engagement, 'engagement');
  if (reach && engagement) out.push(`${reach}, with ${engagement.replace(/^engagement was /, 'engagement of ')}.`);
  else if (reach) out.push(`${reach}.`);
  else if (engagement) out.push(`${engagement.charAt(0).toUpperCase()}${engagement.slice(1)}.`);

  const follower = byKey.follower_growth;
  if (follower && follower.state === 'measured' && follower.value !== null) {
    const net = Math.round(follower.value);
    out.push(
      net >= 0
        ? `The audience grew by ${net.toLocaleString()} followers over the period.`
        : `The audience declined by ${Math.abs(net).toLocaleString()} followers over the period.`,
    );
  }

  const posts = byKey.posts_published;
  if (posts && posts.state === 'measured' && posts.value !== null) {
    const n = Math.round(posts.value);
    out.push(`${n.toLocaleString()} ${n === 1 ? 'post was' : 'posts were'} published.`);
  }

  return out;
}

// ---------------------------------------------------------------------------
// "what happened this period" — real, data-driven highlights (the biggest movers)
// ---------------------------------------------------------------------------

export interface OverviewHighlight {
  key: string;
  /** The metric name ("Reach", "Call clicks", "Reviews"). */
  label: string;
  /** The change phrase ("up 23%", "3 new at 4.8★"). */
  detail: string;
  direction: 'up' | 'down' | 'flat' | null;
}

// Movers below this % change are noise — kept out of the headline highlights.
const HIGHLIGHT_MIN_DELTA_PCT = 3;
// A clean 3-5 line summary, not a wall of numbers.
const MAX_HIGHLIGHTS = 5;

function moverDetail(deltaPct: number, direction: 'up' | 'down' | 'flat' | null): string {
  const pct = Math.abs(deltaPct).toFixed(0);
  if (direction === 'up') return `up ${pct}%`;
  if (direction === 'down') return `down ${pct}%`;
  return 'flat';
}

/**
 * The period's real highlights: the biggest period-over-period movers across ALL
 * platforms (FB/IG reach·engagement·followers·posts, GBP impressions·calls·
 * directions·website, community handled) plus a reviews line — selected from the
 * deltas already computed, ranked by magnitude, capped at a few lines. Deterministic
 * (the AI-narrative home later, but real now). Not-measurable / pending KPIs never
 * appear; a period with nothing measurable returns [] (the tab shows its fallback).
 */
export function buildOverviewHighlights(data: SocialOverview): OverviewHighlight[] {
  const highlights: OverviewHighlight[] = [];

  // Reviews first — the differentiator. Only when reviews were actually received.
  if (data.reviews && data.reviews.current > 0) {
    const { current, previous, averageRating } = data.reviews;
    const { direction } = periodDelta(current, previous);
    const rating = averageRating !== null ? ` at ${averageRating.toFixed(1)}★` : '';
    highlights.push({
      key: 'reviews',
      label: 'Reviews',
      detail: `${current} new review${current === 1 ? '' : 's'}${rating}`,
      direction,
    });
  }

  // Candidate movers with a computable % delta, from the hero cards + GBP KPIs.
  type Mover = { key: string; label: string; deltaPct: number; direction: 'up' | 'down' | 'flat' | null };
  const movers: Mover[] = [];
  for (const card of buildHeroCards(data)) {
    if (card.state === 'measured' && card.deltaPct !== null) {
      movers.push({ key: card.key, label: card.label, deltaPct: card.deltaPct, direction: card.direction });
    }
  }
  if (data.googleBusiness) {
    for (const k of buildGbpHeadlineKpis(data.googleBusiness.metrics, data.range, data.previousRange)) {
      if (k.deltaPct !== null) {
        movers.push({ key: `gbp_${k.key}`, label: k.label, deltaPct: k.deltaPct, direction: k.direction });
      }
    }
  }

  movers
    .filter((m) => Math.abs(m.deltaPct) >= HIGHLIGHT_MIN_DELTA_PCT)
    .sort((a, b) => Math.abs(b.deltaPct) - Math.abs(a.deltaPct))
    .slice(0, MAX_HIGHLIGHTS - highlights.length)
    .forEach((m) =>
      highlights.push({
        key: m.key,
        label: m.label,
        detail: moverDetail(m.deltaPct, m.direction),
        direction: m.direction,
      }),
    );

  return highlights.slice(0, MAX_HIGHLIGHTS);
}
